import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, ShieldCheck, Signature as SignatureIcon } from "@phosphor-icons/react";
import { esignPublic, FIELD_META, ROLE_LABELS } from "@/lib/esignApi";
import SignaturePad from "@/components/app/esign/SignaturePad";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function SignPage() {
  const { inviteId } = useParams();
  const [info, setInfo] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading|otp|sign|done|error
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const [detail, setDetail] = useState(null); // fields payload
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [signature, setSignature] = useState(null);
  const [textVals, setTextVals] = useState({}); // {name,address,date}
  const [witness, setWitness] = useState({ name: "", email: "" });
  const [debugCode, setDebugCode] = useState(null);
  const [sessionMsg, setSessionMsg] = useState(null);
  const tokenRef = useRef(null);
  const pdfWrapRef = useRef(null);
  const [pageW, setPageW] = useState(360);
  const TOKEN_KEY = `signtok:${inviteId}`;

  // Render the PDF at the container's width (capped) so pages + their field
  // boxes are fully visible on mobile without horizontal scrolling.
  useEffect(() => {
    const measure = () => {
      const el = pdfWrapRef.current;
      if (!el) return;
      const avail = el.clientWidth - 24; // minus padding
      setPageW(Math.max(240, Math.min(avail, 720)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [phase, pdfUrl]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await esignPublic.get(`/signing/invite/${inviteId}`);
        if (!data.found) { setPhase("error"); return; }
        setInfo(data);
        if (data.status === "signed") { setPhase("done"); return; }
        // Resume a signing session held from before a reload/pause, so the
        // signer isn't bounced back to the code screen (where re-typing the
        // old, already-used email code would fail forever).
        const saved = sessionStorage.getItem(TOKEN_KEY);
        if (saved) {
          try {
            tokenRef.current = saved;
            setToken(saved);
            await loadDoc(saved);
            setPhase("sign");
            return;
          } catch {
            sessionStorage.removeItem(TOKEN_KEY);
            tokenRef.current = null;
            setToken(null);
          }
        }
        setPhase("otp");
      } catch { setPhase("error"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId]);

  // Signing session (30-min token) lapsed or was lost — route the signer back
  // to request a FRESH code. A previously-emailed code can't be reused.
  const handleSessionLost = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    tokenRef.current = null;
    setToken(null);
    setCode("");
    setDebugCode(null);
    setSessionMsg("Your signing session timed out. Tap \u201cEmail me a code\u201d to get a new code and continue — the previous code can no longer be used.");
    setPhase("otp");
  };

  const requestCode = async () => {
    setBusy(true);
    try {
      const { data } = await esignPublic.post(`/signing/invite/${inviteId}/otp/request`);
      if (data.debug_code) setDebugCode(data.debug_code);
      setSessionMsg(null);
      setCode("");
      toast.success(data.message || "Code sent");
    } catch { toast.error("Could not send code"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await esignPublic.post(`/signing/invite/${inviteId}/otp/verify`, { code });
      tokenRef.current = data.access_token;
      setToken(data.access_token);
      try { sessionStorage.setItem(TOKEN_KEY, data.access_token); } catch { /* storage unavailable */ }
      setSessionMsg(null);
      await loadDoc(data.access_token);
      setPhase("sign");
    } catch (e) {
      setCode("");  // never let them re-submit the same (now-consumed) digits
      toast.error(e.response?.data?.detail || "That code isn\u2019t right. Request a new code.");
    }
    finally { setBusy(false); }
  };

  const loadDoc = async (tk) => {
    const auth = { Authorization: `Bearer ${tk}` };
    const { data: fd } = await esignPublic.get(`/signing/invite/${inviteId}/fields`, { headers: auth });
    setDetail(fd);
    setTextVals({ name: fd.name || "", address: "", date: new Date().toISOString().slice(0, 10) });
    const resp = await esignPublic.get(`/signing/invite/${inviteId}/pdf`, { headers: auth, responseType: "blob" });
    setPdfUrl(URL.createObjectURL(resp.data));
  };

  const submit = async () => {
    const fields = detail.fields || [];
    const hasSig = fields.some((f) => f.type === "signature");
    if (hasSig && !signature) { toast.error("Please type and adopt your signature"); return; }
    if (detail.require_witness && (!witness.name.trim() || !witness.email.trim())) {
      toast.error("Please nominate a witness (name and email)"); return;
    }
    const values = {};
    for (const f of fields) {
      if (f.type === "signature") values[f.field_id] = signature;
      else if (f.type === "name") values[f.field_id] = textVals.name;
      else if (f.type === "address") values[f.field_id] = textVals.address;
      else if (f.type === "date") values[f.field_id] = fmtDate(textVals.date);
    }
    setBusy(true);
    try {
      await esignPublic.post(`/signing/invite/${inviteId}/sign`,
        { values, witness: detail.require_witness ? witness : null },
        { headers: { Authorization: `Bearer ${tokenRef.current}` } });
      try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
      setPhase("done");
    } catch (e) {
      if (e.response?.status === 401) { handleSessionLost(); return; }
      toast.error(e.response?.data?.detail || "Could not submit");
    }
    finally { setBusy(false); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

  const fieldsByType = useCallback((t) => (detail?.fields || []).filter((f) => f.type === t), [detail]);

  // ---- Render ----
  if (phase === "loading") return <Shell><p className="text-slate-500 text-center py-10">Loading…</p></Shell>;
  if (phase === "error") return <Shell><div className="text-center py-10"><p className="text-slate-800 font-medium">This signing link is not valid</p><p className="text-slate-500 text-sm mt-1">It may have been completed, voided, or the link is incorrect. Please contact your conveyancer.</p></div></Shell>;

  if (phase === "done") return (
    <Shell>
      <div className="text-center py-10" data-testid="sign-done">
        <CheckCircle weight="fill" className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
        <h2 className="text-xl font-semibold text-slate-900">Thank you — your signature is recorded</h2>
        <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
          {detail?.signer_kind === "witness"
            ? "Your witness attestation has been captured."
            : detail?.require_witness
              ? "Your nominated witness will now receive an email to complete their attestation."
              : "The document will be finalised by the conveyancer."}
        </p>
      </div>
    </Shell>
  );

  if (phase === "otp") return (
    <Shell>
      <div className="max-w-sm mx-auto py-6" data-testid="sign-otp">
        <div className="text-center mb-5">
          <SignatureIcon weight="bold" className="w-10 h-10 text-slate-800 mx-auto mb-2" />
          <h2 className="text-lg font-semibold text-slate-900">{info.title}</h2>
          <p className="text-slate-500 text-sm mt-1">You've been asked to {info.signer_kind === "witness" ? "witness a signature" : "sign"} as <strong>{ROLE_LABELS[info.role] || info.role}</strong>.</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
          {sessionMsg && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="sign-session-msg">{sessionMsg}</p>
          )}
          <p className="text-sm text-slate-600 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Verify it's you. We'll email a code to <strong>{info.email_masked}</strong>.</p>
          <Button className="w-full" onClick={requestCode} disabled={busy} data-testid="sign-request-code">Email me a code</Button>
          {debugCode && <p className="text-[11px] text-center text-amber-600" data-testid="sign-debug-code">Preview test code: {debugCode}</p>}
          <div>
            <Label className="text-xs">Enter the 6-digit code</Label>
            <Input inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••••" className="text-center tracking-[0.5em] text-lg" data-testid="sign-code-input" />
          </div>
          <Button className="w-full" onClick={verify} disabled={busy || code.length !== 6} data-testid="sign-verify">Continue</Button>
        </div>
      </div>
    </Shell>
  );

  // phase === "sign"
  const hasSig = fieldsByType("signature").length > 0;
  return (
    <Shell wide>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6" data-testid="sign-surface">
        {/* Document preview */}
        <div ref={pdfWrapRef} className="bg-slate-200 rounded-lg p-3 overflow-x-hidden overflow-y-auto max-h-[70vh] lg:max-h-[80vh]">
          {pdfUrl ? (
            <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<p className="text-center text-slate-500 py-8">Loading document…</p>}>
              {Array.from({ length: numPages }, (_, idx) => (
                <div key={idx} className="mx-auto mb-4 relative bg-white shadow" style={{ width: pageW }}>
                  <Page pageNumber={idx + 1} width={pageW} renderTextLayer={false} renderAnnotationLayer={false} />
                  {(detail?.fields || []).filter((f) => f.page === idx).map((f) => (
                    <div key={f.field_id} className="absolute rounded-[3px] flex items-center justify-center overflow-hidden"
                      style={{ left: `${f.x * 100}%`, top: `${f.y * 100}%`, width: `${f.w * 100}%`, height: `${f.h * 100}%`,
                               border: "2px solid #f59e0b", background: "rgba(245,158,11,0.22)",
                               boxShadow: "0 0 0 2px rgba(245,158,11,0.25)" }}>
                      <span className="text-[9px] font-bold uppercase tracking-wide text-amber-900 whitespace-nowrap px-0.5">
                        {f.type === "signature" ? "✎ Sign" : FIELD_META[f.type]?.label}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </Document>
          ) : <p className="text-center text-slate-500 py-8">Loading document…</p>}
        </div>

        {/* Fill panel */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-slate-900 text-sm">Complete your details</h3>
            {fieldsByType("name").length > 0 && (
              <div><Label className="text-xs">Full name</Label>
                <Input value={textVals.name} onChange={(e) => setTextVals({ ...textVals, name: e.target.value })} data-testid="sign-name" /></div>
            )}
            {fieldsByType("address").length > 0 && (
              <div><Label className="text-xs">Address</Label>
                <Input value={textVals.address} onChange={(e) => setTextVals({ ...textVals, address: e.target.value })} placeholder="Your address" data-testid="sign-address" /></div>
            )}
            {fieldsByType("date").length > 0 && (
              <div><Label className="text-xs">Date</Label>
                <Input type="date" value={textVals.date} onChange={(e) => setTextVals({ ...textVals, date: e.target.value })} data-testid="sign-date" /></div>
            )}
            {hasSig && (
              <div><Label className="text-xs mb-1 block">Your signature</Label>
                <SignaturePad defaultName={detail.name} onChange={setSignature} signatureCount={fieldsByType("signature").length} /></div>
            )}
          </div>

          {detail?.require_witness && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 space-y-2">
              <h3 className="font-semibold text-cyan-900 text-sm">Nominate your witness</h3>
              <p className="text-xs text-cyan-700">This is a deed — your signature must be witnessed. After you sign, your witness will be emailed to attest.</p>
              <div><Label className="text-xs">Witness full name</Label>
                <Input value={witness.name} onChange={(e) => setWitness({ ...witness, name: e.target.value })} data-testid="sign-witness-name" /></div>
              <div><Label className="text-xs">Witness email</Label>
                <Input value={witness.email} onChange={(e) => setWitness({ ...witness, email: e.target.value })} data-testid="sign-witness-email" /></div>
            </div>
          )}

          <Button className="w-full" size="lg" onClick={submit} disabled={busy} data-testid="sign-submit">
            {detail?.signer_kind === "witness" ? "Confirm & witness" : "Sign document"}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">By signing you agree your electronic signature is legally binding.</p>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white py-3 px-5">
        <span className="font-semibold tracking-wide text-sm">UKC LEGAL · Secure Signing</span>
      </div>
      <div className={`mx-auto px-4 py-6 ${wide ? "max-w-6xl" : "max-w-2xl"}`}>{children}</div>
    </div>
  );
}
